import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Chip,
  Avatar,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Alert,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import {
  SearchOutlined,
  PersonAddOutlined,
  UploadFileOutlined,
  EditOutlined,
  VpnKeyOutlined,
  BlockOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  ContentCopyOutlined,
  FiberManualRecord,
  DownloadOutlined,
  PeopleOutlined,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import adminService from '../../services/admin.service';
import { useNotification } from '../../context/NotificationContext';
import { roleColors, roleAvatarColors } from '../../utils/roleColors';
import { getInitials } from '../../utils/getInitials';

interface UserRow {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  authMethod: string;
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

const AdminUsers: React.FC = () => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  // Table state
  const [users, setUsers] = useState<UserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [loading, setLoading] = useState(true);

  // Dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [resetPwOpen, setResetPwOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [passwordResult, setPasswordResult] = useState<{ email: string; tempPassword: string } | null>(null);

  // Create form
  const [createForm, setCreateForm] = useState({ firstName: '', lastName: '', email: '', role: 'student', password: '' });
  const [createError, setCreateError] = useState('');
  const [createLoading, setCreateLoading] = useState(false);

  // Edit form
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '', email: '', role: '' });
  const [editError, setEditError] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  // Reset pw
  const [resetMode, setResetMode] = useState<'random' | 'specific'>('random');
  const [specificPassword, setSpecificPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  // Delete
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Status toggle
  const [statusLoading, setStatusLoading] = useState(false);

  // Bulk import
  const [bulkStep, setBulkStep] = useState<'upload' | 'preview' | 'result'>('upload');
  const [bulkRows, setBulkRows] = useState<Array<{ email: string; firstName: string; lastName: string; role: string; error?: string }>>([]);
  const [bulkResult, setBulkResult] = useState<{ created: Array<{ email: string; tempPassword: string }>; skipped: number; errors: Array<{ email: string; reason: string }> } | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  const [copied, setCopied] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminService.getUsers({
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined,
        role: roleFilter !== 'all' ? roleFilter : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        sortBy,
        sortOrder,
      });
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch {
      showNotification('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, roleFilter, statusFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Debounced search
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ---- CREATE USER ----
  const handleCreate = async () => {
    setCreateError('');
    setCreateLoading(true);
    try {
      const res = await adminService.createUser({
        email: createForm.email,
        firstName: createForm.firstName,
        lastName: createForm.lastName,
        role: createForm.role,
        password: createForm.password || undefined,
      });
      setCreateOpen(false);
      setPasswordResult({ email: createForm.email, tempPassword: res.data.tempPassword });
      setCreateForm({ firstName: '', lastName: '', email: '', role: 'student', password: '' });
      fetchUsers();
    } catch (err: any) {
      setCreateError(err.response?.data?.message || 'Failed to create user');
    } finally {
      setCreateLoading(false);
    }
  };

  // ---- EDIT USER ----
  const openEdit = (u: UserRow) => {
    setSelectedUser(u);
    setEditForm({ firstName: u.firstName, lastName: u.lastName, email: u.email, role: u.role });
    setEditError('');
    setEditOpen(true);
  };

  const handleEdit = async () => {
    if (!selectedUser) return;
    setEditError('');
    setEditLoading(true);
    try {
      await adminService.updateUser(selectedUser.id, editForm);
      setEditOpen(false);
      fetchUsers();
    } catch (err: any) {
      setEditError(err.response?.data?.message || 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  // ---- RESET PASSWORD ----
  const openResetPw = (u: UserRow) => {
    setSelectedUser(u);
    setResetMode('random');
    setSpecificPassword('');
    setResetPwOpen(true);
  };

  const handleResetPw = async () => {
    if (!selectedUser) return;
    setResetLoading(true);
    try {
      const data = resetMode === 'specific' ? { password: specificPassword } : undefined;
      const res = await adminService.resetUserPassword(selectedUser.id, data);
      setResetPwOpen(false);
      setPasswordResult({ email: selectedUser.email, tempPassword: res.data.tempPassword });
    } catch {
      showNotification('Failed to reset password', 'error');
    } finally {
      setResetLoading(false);
    }
  };

  // ---- STATUS TOGGLE ----
  const openStatusToggle = (u: UserRow) => {
    setSelectedUser(u);
    setStatusDialogOpen(true);
  };

  const handleStatusToggle = async () => {
    if (!selectedUser) return;
    setStatusLoading(true);
    try {
      await adminService.updateUserStatus(selectedUser.id, { isActive: !selectedUser.isActive });
      setStatusDialogOpen(false);
      fetchUsers();
    } catch {
      showNotification('Failed to update user status', 'error');
    } finally {
      setStatusLoading(false);
    }
  };

  // ---- DELETE ----
  const openDelete = (u: UserRow) => {
    setSelectedUser(u);
    setDeleteConfirmEmail('');
    setDeleteError('');
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    setDeleteError('');
    setDeleteLoading(true);
    try {
      await adminService.deleteUser(selectedUser.id);
      setDeleteOpen(false);
      fetchUsers();
    } catch (err: any) {
      setDeleteError(err.response?.data?.message || 'Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };

  // ---- BULK IMPORT ----
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const lines = text.split('\n').filter((l) => l.trim());
      if (lines.length < 2) return;

      const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
      const emailIdx = headers.indexOf('email');
      const fnIdx = headers.indexOf('firstname');
      const lnIdx = headers.indexOf('lastname');
      const roleIdx = headers.indexOf('role');

      if (emailIdx === -1 || fnIdx === -1 || lnIdx === -1 || roleIdx === -1) {
        setBulkRows([{ email: '', firstName: '', lastName: '', role: '', error: 'CSV must have columns: email, firstName, lastName, role' }]);
        setBulkStep('preview');
        return;
      }

      const rows = lines.slice(1).map((line) => {
        const cols = line.split(',').map((c) => c.trim());
        const row = {
          email: cols[emailIdx] || '',
          firstName: cols[fnIdx] || '',
          lastName: cols[lnIdx] || '',
          role: cols[roleIdx]?.toLowerCase() || '',
          error: undefined as string | undefined,
        };
        if (!row.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) row.error = 'Invalid email';
        else if (!row.firstName || !row.lastName) row.error = 'Missing name';
        else if (!['student', 'instructor', 'admin'].includes(row.role)) row.error = `Invalid role: ${row.role}`;
        return row;
      });

      // Check for duplicates
      const seen = new Set<string>();
      rows.forEach((r) => {
        if (!r.error && seen.has(r.email.toLowerCase())) r.error = 'Duplicate email';
        seen.add(r.email.toLowerCase());
      });

      setBulkRows(rows);
      setBulkStep('preview');
    };
    reader.readAsText(file);
  };

  const handleBulkImport = async () => {
    setBulkLoading(true);
    try {
      const validRows = bulkRows.filter((r) => !r.error);
      const res = await adminService.bulkImportUsers({ users: validRows });
      setBulkResult(res.data);
      setBulkStep('result');
      fetchUsers();
    } catch {
      showNotification('Failed to import users', 'error');
    } finally {
      setBulkLoading(false);
    }
  };

  const downloadCsvTemplate = () => {
    const csv = 'email,firstName,lastName,role\nuser@example.com,John,Doe,student\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'user-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPasswordsCsv = () => {
    if (!bulkResult) return;
    const lines = ['email,tempPassword', ...bulkResult.created.map((c) => `${c.email},${c.tempPassword}`)];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'imported-user-passwords.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const closeBulk = () => {
    setBulkOpen(false);
    setBulkStep('upload');
    setBulkRows([]);
    setBulkResult(null);
  };

  const isSelf = (u: UserRow) => u.id === currentUser?.id;
  const validCount = bulkRows.filter((r) => !r.error).length;
  const errorCount = bulkRows.filter((r) => r.error).length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          User Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button variant="outlined" startIcon={<UploadFileOutlined />} onClick={() => setBulkOpen(true)}>
            Bulk Import
          </Button>
          <Button variant="contained" startIcon={<PersonAddOutlined />} onClick={() => setCreateOpen(true)}>
            Create User
          </Button>
        </Box>
      </Box>

      {/* Search + Filters */}
      <Paper sx={{ p: 2, mb: 2.5, borderRadius: '12px' }} elevation={0}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search by name or email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ color: 'text.secondary', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {['all', 'admin', 'instructor', 'student'].map((r) => (
              <Chip
                key={r}
                label={r.charAt(0).toUpperCase() + r.slice(1)}
                size="small"
                variant={roleFilter === r ? 'filled' : 'outlined'}
                color={r === 'all' ? 'default' : roleColors[r] || 'default'}
                onClick={() => { setRoleFilter(r); setPage(0); }}
                sx={{ fontWeight: roleFilter === r ? 600 : 400 }}
              />
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {['all', 'active', 'inactive'].map((s) => (
              <Chip
                key={s}
                label={s.charAt(0).toUpperCase() + s.slice(1)}
                size="small"
                variant={statusFilter === s ? 'filled' : 'outlined'}
                onClick={() => { setStatusFilter(s); setPage(0); }}
                sx={{ fontWeight: statusFilter === s ? 600 : 400 }}
              />
            ))}
          </Box>
        </Box>
      </Paper>

      {/* Table */}
      <Paper sx={{ borderRadius: '12px', overflow: 'hidden' }} elevation={0}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel active={sortBy === 'email'} direction={sortBy === 'email' ? sortOrder : 'asc'} onClick={() => handleSort('email')}>
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel active={sortBy === 'role'} direction={sortBy === 'role' ? sortOrder : 'asc'} onClick={() => handleSort('role')}>
                    Role
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel active={sortBy === 'createdAt'} direction={sortBy === 'createdAt' ? sortOrder : 'asc'} onClick={() => handleSort('createdAt')}>
                    Registered
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel active={sortBy === 'lastLoginAt'} direction={sortBy === 'lastLoginAt' ? sortOrder : 'asc'} onClick={() => handleSort('lastLoginAt')}>
                    Last Login
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <PeopleOutlined sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">No users found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((u) => {
                  const initials = getInitials(u.firstName, u.lastName);
                  return (
                    <TableRow
                      key={u.id}
                      hover
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8f9fa' } }}
                      onClick={() => navigate(`/admin/users/${u.id}`)}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 36, height: 36, bgcolor: roleAvatarColors[u.role] || '#999', fontSize: '0.8rem', fontWeight: 600 }}>
                            {initials}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {u.firstName} {u.lastName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{u.email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={u.role} size="small" color={roleColors[u.role] || 'default'} sx={{ textTransform: 'capitalize', fontWeight: 500 }} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <FiberManualRecord sx={{ fontSize: 10, color: u.isActive ? '#22c55e' : '#ef4444' }} />
                          <Typography variant="body2" color="text.secondary">
                            {u.isActive ? 'Active' : 'Inactive'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{dayjs(u.createdAt).fromNow()}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {u.lastLoginAt ? dayjs(u.lastLoginAt).fromNow() : 'Never'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(u)}><EditOutlined sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                        <Tooltip title="Reset Password"><IconButton size="small" onClick={() => openResetPw(u)}><VpnKeyOutlined sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                        {!isSelf(u) && (
                          <Tooltip title={u.isActive ? 'Deactivate' : 'Activate'}>
                            <IconButton size="small" onClick={() => openStatusToggle(u)}>
                              {u.isActive ? <BlockOutlined sx={{ fontSize: 18 }} /> : <CheckCircleOutlined sx={{ fontSize: 18, color: '#22c55e' }} />}
                            </IconButton>
                          </Tooltip>
                        )}
                        {!isSelf(u) && (
                          <Tooltip title="Delete"><IconButton size="small" onClick={() => openDelete(u)} sx={{ color: 'error.main' }}><DeleteOutlined sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Paper>

      {/* ---- CREATE USER DIALOG ---- */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>Create New User</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          {createError && <Alert severity="error" sx={{ mb: 1 }}>{createError}</Alert>}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="First Name" fullWidth size="small" value={createForm.firstName} onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })} required inputProps={{ maxLength: 100 }} />
            <TextField label="Last Name" fullWidth size="small" value={createForm.lastName} onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })} required inputProps={{ maxLength: 100 }} />
          </Box>
          <TextField label="Email" fullWidth size="small" type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} required />
          <FormControl size="small" fullWidth>
            <InputLabel>Role</InputLabel>
            <Select value={createForm.role} label="Role" onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}>
              <MenuItem value="student">Student</MenuItem>
              <MenuItem value="instructor">Instructor</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Password"
            fullWidth
            size="small"
            value={createForm.password}
            onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
            helperText="Leave blank to auto-generate"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={createLoading || !createForm.email || !createForm.firstName || !createForm.lastName}>
            {createLoading ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- PASSWORD RESULT DIALOG ---- */}
      <Dialog open={!!passwordResult} onClose={() => setPasswordResult(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Password Generated</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>This is the only time this password will be shown. Copy it now.</Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>User: {passwordResult?.email}</Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderRadius: '8px',
              bgcolor: 'background.default',
            }}
          >
            <Typography variant="h6" sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>
              {passwordResult?.tempPassword}
            </Typography>
            <Tooltip title={copied ? 'Copied!' : 'Copy'}>
              <IconButton onClick={() => copyToClipboard(passwordResult?.tempPassword || '')}>
                <ContentCopyOutlined />
              </IconButton>
            </Tooltip>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="contained" onClick={() => setPasswordResult(null)}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* ---- EDIT USER DIALOG ---- */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>Edit User</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          {editError && <Alert severity="error" sx={{ mb: 1 }}>{editError}</Alert>}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="First Name" fullWidth size="small" value={editForm.firstName} onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })} />
            <TextField label="Last Name" fullWidth size="small" value={editForm.lastName} onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })} />
          </Box>
          <TextField label="Email" fullWidth size="small" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
          <FormControl size="small" fullWidth>
            <InputLabel>Role</InputLabel>
            <Select value={editForm.role} label="Role" onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}>
              <MenuItem value="student">Student</MenuItem>
              <MenuItem value="instructor">Instructor</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
          {selectedUser && editForm.role !== selectedUser.role && (
            <Alert severity="warning">
              Changing role from <strong>{selectedUser.role}</strong> to <strong>{editForm.role}</strong>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit} disabled={editLoading}>
            {editLoading ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- RESET PASSWORD DIALOG ---- */}
      <Dialog open={resetPwOpen} onClose={() => setResetPwOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Reset Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Reset password for <strong>{selectedUser?.firstName} {selectedUser?.lastName}</strong>
          </Typography>
          <RadioGroup value={resetMode} onChange={(e) => setResetMode(e.target.value as 'random' | 'specific')}>
            <FormControlLabel value="random" control={<Radio size="small" />} label="Generate random password" />
            <FormControlLabel value="specific" control={<Radio size="small" />} label="Set specific password" />
          </RadioGroup>
          {resetMode === 'specific' && (
            <TextField
              label="New Password"
              fullWidth
              size="small"
              sx={{ mt: 1.5 }}
              value={specificPassword}
              onChange={(e) => setSpecificPassword(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setResetPwOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleResetPw}
            disabled={resetLoading || (resetMode === 'specific' && !specificPassword)}
          >
            {resetLoading ? <CircularProgress size={20} /> : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- STATUS TOGGLE DIALOG ---- */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {selectedUser?.isActive ? 'Deactivate User' : 'Reactivate User'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {selectedUser?.isActive
              ? `Deactivate ${selectedUser?.firstName} ${selectedUser?.lastName}? They will not be able to log in.`
              : `Reactivate ${selectedUser?.firstName} ${selectedUser?.lastName}?`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={selectedUser?.isActive ? 'warning' : 'success'}
            onClick={handleStatusToggle}
            disabled={statusLoading}
          >
            {statusLoading ? <CircularProgress size={20} /> : selectedUser?.isActive ? 'Deactivate' : 'Reactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- DELETE DIALOG ---- */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ fontWeight: 600, color: 'error.main' }}>Delete User</DialogTitle>
        <DialogContent>
          {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
          <Typography variant="body2" sx={{ mb: 2 }}>
            This will permanently remove <strong>{selectedUser?.firstName} {selectedUser?.lastName}</strong>. This action cannot be undone.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Type <strong>{selectedUser?.email}</strong> to confirm:
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={deleteConfirmEmail}
            onChange={(e) => setDeleteConfirmEmail(e.target.value)}
            placeholder={selectedUser?.email}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleteLoading || deleteConfirmEmail !== selectedUser?.email}
          >
            {deleteLoading ? <CircularProgress size={20} /> : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- BULK IMPORT DIALOG ---- */}
      <Dialog open={bulkOpen} onClose={closeBulk} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>
          Bulk Import Users
          {bulkStep === 'preview' && ` \u2014 Preview`}
          {bulkStep === 'result' && ` \u2014 Results`}
        </DialogTitle>
        <DialogContent>
          {bulkStep === 'upload' && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload a CSV file with columns: email, firstName, lastName, role
              </Typography>
              <Button variant="text" size="small" onClick={downloadCsvTemplate} startIcon={<DownloadOutlined />} sx={{ mb: 2 }}>
                Download CSV template
              </Button>
              <Paper
                variant="outlined"
                sx={{
                  p: 4,
                  textAlign: 'center',
                  borderStyle: 'dashed',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'background.default' },
                }}
                onClick={() => document.getElementById('bulk-csv-input')?.click()}
              >
                <UploadFileOutlined sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Click to select a .csv file
                </Typography>
                <input
                  id="bulk-csv-input"
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={handleFileUpload}
                />
              </Paper>
            </Box>
          )}

          {bulkStep === 'preview' && (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Chip label={`${validCount} valid`} color="success" size="small" />
                {errorCount > 0 && <Chip label={`${errorCount} errors`} color="error" size="small" />}
              </Box>
              <TableContainer sx={{ maxHeight: 350 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>First Name</TableCell>
                      <TableCell>Last Name</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bulkRows.map((r, i) => (
                      <TableRow key={i} sx={{ bgcolor: r.error ? 'error.lighter' : undefined }}>
                        <TableCell>{r.email}</TableCell>
                        <TableCell>{r.firstName}</TableCell>
                        <TableCell>{r.lastName}</TableCell>
                        <TableCell>{r.role}</TableCell>
                        <TableCell>
                          {r.error ? (
                            <Typography variant="caption" color="error">{r.error}</Typography>
                          ) : (
                            <Typography variant="caption" color="success.main">Valid</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {bulkStep === 'result' && bulkResult && (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Created {bulkResult.created.length} users
                {bulkResult.skipped > 0 && `, skipped ${bulkResult.skipped}`}
                {bulkResult.errors.length > 0 && `, ${bulkResult.errors.length} errors`}
              </Alert>
              {bulkResult.created.length > 0 && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">Generated Passwords</Typography>
                    <Button size="small" startIcon={<DownloadOutlined />} onClick={downloadPasswordsCsv}>
                      Download CSV
                    </Button>
                  </Box>
                  <TableContainer sx={{ maxHeight: 250 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Email</TableCell>
                          <TableCell>Password</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bulkResult.created.map((c) => (
                          <TableRow key={c.email}>
                            <TableCell>{c.email}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{c.tempPassword}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
              {bulkResult.errors.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>Errors</Typography>
                  {bulkResult.errors.map((e, i) => (
                    <Typography key={i} variant="caption" display="block" color="text.secondary">
                      {e.email}: {e.reason}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          {bulkStep === 'upload' && <Button onClick={closeBulk}>Cancel</Button>}
          {bulkStep === 'preview' && (
            <>
              <Button onClick={() => { setBulkStep('upload'); setBulkRows([]); }}>Back</Button>
              <Button variant="contained" onClick={handleBulkImport} disabled={bulkLoading || validCount === 0}>
                {bulkLoading ? <CircularProgress size={20} /> : `Import ${validCount} Users`}
              </Button>
            </>
          )}
          {bulkStep === 'result' && <Button variant="contained" onClick={closeBulk}>Done</Button>}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUsers;
